import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  CircularProgress, 
  Button, 
  Paper,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PaymentIcon from '@mui/icons-material/Payment';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';
import clientsApi from '../../api/clientsApi';
import { formatCurrency } from '../../utils/currency';
import PaginatedTable from '../../components/PaginatedTable';
import SortableTableHeader from '../../components/SortableTableHeader';

const ClientPayments = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [totalPayments, setTotalPayments] = useState(0);
  const [sortField, setSortField] = useState('payment_date');
  const [sortDirection, setSortDirection] = useState('desc');

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch client details and payments with pagination and sorting
      const [clientData, paymentsData] = await Promise.all([
        clientsApi.getClient(id),
        clientsApi.getClientPayments(id, {
          skip: page * pageSize,
          limit: pageSize,
          order_by: sortField,
          order_direction: sortDirection
        })
      ]);
      
      setClient(clientData);
      setPayments(paymentsData.payments || []);
      setTotalPayments(paymentsData.total || 0);
      setError(null);
    } catch (err) {
      console.error('Error fetching client payments:', err);
      setError('Failed to load client payments. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      fetchData();
    }
  }, [id, page, pageSize, sortField, sortDirection]);

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setPage(0); // Reset to first page when changing page size
  };

  const handleSort = (field, direction) => {
    setSortField(field);
    setSortDirection(direction);
    setPage(0); // Reset to first page when sorting changes
  };

  const handleBack = () => {
    navigate(`/clients/${id}`);
  };

  const handleViewPayment = (payment) => {
    setSelectedPayment(payment);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedPayment(null);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not provided';
    return new Date(dateString).toLocaleDateString();
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed':
      case 'paid':
        return 'success';
      case 'pending':
        return 'warning';
      case 'failed':
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Button startIcon={<ArrowBackIcon />} onClick={handleBack} sx={{ mb: 2 }}>
          Back to Client
        </Button>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Button startIcon={<ArrowBackIcon />} onClick={handleBack}>
          Back to Client
        </Button>
      </Box>

      <PaginatedTable
        title={`Payments for ${client?.full_name}`}
        icon={<PaymentIcon color="primary" sx={{ mr: 1 }} />}
        total={totalPayments}
        page={page}
        pageSize={pageSize}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        loading={loading}
      >
        {payments.length === 0 ? (
          <Alert severity="info">No payments found for this client.</Alert>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <SortableTableHeader
                    field="payment_id"
                    label="Payment ID"
                    sortField={sortField}
                    sortDirection={sortDirection}
                    onSort={handleSort}
                  />
                  <SortableTableHeader
                    field="amount"
                    label="Amount"
                    sortField={sortField}
                    sortDirection={sortDirection}
                    onSort={handleSort}
                  />
                  <SortableTableHeader
                    field="payment_type_name"
                    label="Payment Type"
                    sortField={sortField}
                    sortDirection={sortDirection}
                    onSort={handleSort}
                  />
                  <SortableTableHeader
                    field="status"
                    label="Status"
                    sortField={sortField}
                    sortDirection={sortDirection}
                    onSort={handleSort}
                  />
                  <SortableTableHeader
                    field="payment_date"
                    label="Payment Date"
                    sortField={sortField}
                    sortDirection={sortDirection}
                    onSort={handleSort}
                  />
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {payments.map((payment) => (
                  <TableRow key={payment.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {payment.payment_id || 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {formatCurrency(payment.amount)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {payment.payment_type_name || 'Unknown'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={payment.status || 'Unknown'} 
                        color={getStatusColor(payment.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{formatDate(payment.payment_date)}</TableCell>
                    <TableCell>
                      <Tooltip title="View Details">
                        <IconButton 
                          size="small"
                          onClick={() => handleViewPayment(payment)}
                        >
                          <VisibilityIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </PaginatedTable>

      <Dialog open={modalOpen} onClose={handleCloseModal} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Payment Details</Typography>
            <IconButton onClick={handleCloseModal} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedPayment && (
            <Box sx={{ pt: 1 }}>
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Payment {selectedPayment.payment_id || 'N/A'}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                  <Chip 
                    label={selectedPayment.status || 'Unknown'} 
                    color={getStatusColor(selectedPayment.status)}
                    size="small"
                  />
                  {selectedPayment.payment_type_name && (
                    <Chip 
                      label={selectedPayment.payment_type_name} 
                      color="default"
                      size="small"
                    />
                  )}
                </Box>
              </Box>
              
              <Divider sx={{ mb: 2 }} />
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Payment Information
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Amount:</Typography>
                    <Typography variant="body1" fontWeight="medium">{formatCurrency(selectedPayment.amount)}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Fee Amount:</Typography>
                    <Typography variant="body1">{formatCurrency(selectedPayment.fee_amount)}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Payment Date:</Typography>
                    <Typography variant="body1">{formatDate(selectedPayment.payment_date)}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Status:</Typography>
                    <Typography variant="body1">{selectedPayment.status || 'Unknown'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Payment Type:</Typography>
                    <Typography variant="body1">{selectedPayment.payment_type_name || 'Unknown'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Created Date:</Typography>
                    <Typography variant="body1">{formatDate(selectedPayment.created_date)}</Typography>
                  </Box>
                </Box>
              </Box>
              
              {selectedPayment.payment_note && (
                <>
                  <Divider sx={{ mb: 2 }} />
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Payment Note
                    </Typography>
                    <Paper 
                      variant="outlined" 
                      sx={{ 
                        p: 2, 
                        backgroundColor: 'grey.50',
                        maxHeight: '300px',
                        overflow: 'auto'
                      }}
                    >
                      <Typography variant="body1" style={{ whiteSpace: 'pre-wrap' }}>
                        {selectedPayment.payment_note}
                      </Typography>
                    </Paper>
                  </Box>
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ClientPayments; 